import type {
  RecoveryCircle,
  RecoveryDecision,
  RecoveryRequest,
  RecoveryRequestStatus,
  RecoveryUser,
  RecoveryUserId,
} from './types.ts';

export function sameRecoveryId(
  left: RecoveryUserId | undefined | null,
  right: RecoveryUserId | undefined | null
): boolean {
  return left !== undefined &&
    left !== null &&
    right !== undefined &&
    right !== null &&
    String(left) === String(right);
}

export function eligibleRecoveryUsers(
  users: RecoveryUser[],
  currentUserId?: RecoveryUserId
): RecoveryUser[] {
  const seen = new Set<string>();
  return users.filter((user) => {
    const id = String(user.id);
    if (sameRecoveryId(user.id, currentUserId) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function thresholdOptions(memberCount: number): number[] {
  if (memberCount < 2) return [];
  return Array.from({ length: memberCount - 1 }, (_, index) => index + 2);
}

export function validateCircleSetup(
  selectedUserIds: RecoveryUserId[],
  currentUserId: RecoveryUserId | undefined,
  threshold: number
): string[] {
  const errors: string[] = [];
  const normalized = selectedUserIds.map(String);
  const unique = new Set(normalized);

  if (selectedUserIds.length < 2) {
    errors.push('Choose at least two personal keyholders.');
  }
  if (selectedUserIds.length > 8) {
    errors.push('Choose no more than eight personal keyholders.');
  }
  if (unique.size !== selectedUserIds.length) {
    errors.push('Each person can be selected only once.');
  }
  if (
    currentUserId !== undefined &&
    normalized.includes(String(currentUserId))
  ) {
    errors.push('You cannot be a keyholder in your own recovery circle.');
  }
  if (
    !Number.isInteger(threshold) ||
    threshold < 2 ||
    threshold > selectedUserIds.length
  ) {
    errors.push('The threshold must be between two and the number of keyholders.');
  }
  return errors;
}

export function circleStatusLabel(status: RecoveryCircle['status']): string {
  const labels: Record<RecoveryCircle['status'], string> = {
    draft: 'Draft',
    inviting: 'Waiting for responses',
    ready: 'Ready to activate',
    active: 'Active',
    rotation_required: 'Rotation required',
    disabled: 'Disabled',
  };
  return labels[status];
}

export function requestStatusLabel(status: RecoveryRequestStatus): string {
  const labels: Record<RecoveryRequestStatus, string> = {
    pending_delay: 'Safety delay',
    awaiting_approvals: 'Awaiting approvals',
    ready: 'Threshold reached',
    completed: 'Completed',
    cancelled: 'Cancelled',
    denied: 'Denied',
    reported: 'Reported',
    expired: 'Expired',
    invalid: 'Invalid state',
  };
  return labels[status];
}

export function requestIsActive(request: RecoveryRequest): boolean {
  return (
    ['pending_delay', 'awaiting_approvals', 'ready'].includes(request.status) ||
    (request.status === 'reported' && request.permissions?.cancel === true)
  );
}

export interface CircleReadiness {
  accepted: number;
  invited: number;
  declined: number;
  total: number;
  canActivate: boolean;
  canRotate: boolean;
}

export function getCircleReadiness(circle: RecoveryCircle): CircleReadiness {
  const accepted = circle.members.filter(
    (member) => member.status === 'accepted'
  ).length;
  const invited = circle.members.filter(
    (member) => member.status === 'invited'
  ).length;
  const declined = circle.members.filter(
    (member) => member.status === 'declined'
  ).length;
  return {
    accepted,
    invited,
    declined,
    total: circle.members.length,
    canActivate:
      circle.members.length >= 2 &&
      accepted === circle.members.length &&
      !circle.rotationRequired &&
      circle.status !== 'active' &&
      circle.status !== 'disabled',
    canRotate:
      circle.rotationRequired &&
      circle.status === 'active' &&
      accepted === circle.members.length &&
      accepted >= 2,
  };
}

export function circleForRotation(circle: RecoveryCircle): RecoveryCircle {
  if (!circle.version || circle.version < 1) {
    throw new Error('Rotation needs the current circle version from the server.');
  }
  return {
    ...circle,
    version: circle.version + 1,
    rotationRequired: false,
    status: 'ready',
  };
}

function validDate(value: string): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value?: string | null): string {
  if (!value) return 'Not provided';
  const date = validDate(value);
  if (!date) return 'Invalid date';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function formatDuration(milliseconds: number): string {
  const totalMinutes = Math.max(1, Math.ceil(Math.abs(milliseconds) / 60_000));
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours ? `${days} d ${remainingHours} hr` : `${days} d`;
}

export interface RecoveryRequestTiming {
  phase: 'waiting' | 'available' | 'expired' | 'unknown';
  summary: string;
}

export function getRecoveryRequestTiming(
  request: Pick<RecoveryRequest, 'availableAt' | 'expiresAt'>,
  now = Date.now()
): RecoveryRequestTiming {
  const available = validDate(request.availableAt)?.getTime();
  const expiry = validDate(request.expiresAt)?.getTime();
  if (available === undefined || expiry === undefined || available === null || expiry === null) {
    return { phase: 'unknown', summary: 'Schedule unavailable' };
  }
  if (now < available) {
    return {
      phase: 'waiting',
      summary: `Available in ${formatDuration(available - now)}`,
    };
  }
  if (now >= expiry) {
    return { phase: 'expired', summary: 'Approval window expired' };
  }
  return {
    phase: 'available',
    summary: `Available now · expires in ${formatDuration(expiry - now)}`,
  };
}

export function canDecideRecoveryRequest(
  request: RecoveryRequest,
  decision: RecoveryDecision,
  now = Date.now()
): boolean {
  const permission = request.permissions?.[decision];
  const timing = getRecoveryRequestTiming(request, now);
  if (permission !== undefined) {
    if (!permission || timing.phase === 'expired') return false;
    return decision !== 'approve' || timing.phase === 'available';
  }
  if (request.viewerRole !== 'keyholder' || request.myDecision) return false;

  if (decision === 'approve') {
    return (
      request.status === 'awaiting_approvals' && timing.phase === 'available'
    );
  }
  return (
    ['pending_delay', 'awaiting_approvals'].includes(request.status) &&
    timing.phase !== 'expired'
  );
}

export function canCancelRecoveryRequest(request: RecoveryRequest): boolean {
  if (request.permissions) return request.permissions.cancel;
  return (
    request.viewerRole === 'owner' &&
    ['pending_delay', 'awaiting_approvals', 'ready'].includes(request.status)
  );
}

export function canCompleteRecoveryRequest(request: RecoveryRequest): boolean {
  if (request.permissions) return request.permissions.complete;
  return request.viewerRole === 'owner' && request.status === 'ready';
}
